import { useTheme } from '../components/PopcornProvider'
import { useMediaQuery } from './useMediaQuery'

/**
 * Hook that returns whether dark mode is currently active.
 *
 * Handles all theme variants:
 * - Returns `true` for 'dark' theme
 * - Returns `false` for 'light' theme
 * - For 'system' theme, returns the OS preference via `prefers-color-scheme`
 *
 * The hook automatically listens to OS preference changes when theme is set to 'system',
 * ensuring the dark mode state updates dynamically if the user changes their system settings.
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const isDarkMode = useIsDarkMode()
 *
 *   return (
 *     <div style={{ background: isDarkMode ? '#000' : '#fff' }}>
 *       Current mode: {isDarkMode ? 'Dark' : 'Light'}
 *     </div>
 *   )
 * }
 * ```
 */
export function useIsDarkMode(): boolean {
  const { theme } = useTheme()
  const prefersColorSchemeDark = useMediaQuery('(prefers-color-scheme: dark)')

  if (theme === 'system') {
    return prefersColorSchemeDark
  }

  return theme === 'dark'
}
